import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import classnames from 'classnames';
import {withRouter} from 'react-router';
import {isEmpty} from 'lodash';
import {withApplicationMetadata} from 'client/Routing/withApplicationMetadata';
import sessionStore from 'client/Session/SessionStore';
import {useFluxStore} from 'client/framework';
import constants from 'client/constants';
import appStore from 'client/AppStore';
import {getQueryParamsAsObject, removeQueryParams} from 'client/history';

import {
  canCreateTemplateForSubject,
  getUserSubjectAccessQuery,
  isUnlicensedTeacherOrAdmin,
  userLicensedSubjectsQuery
} from 'lib/UserAccessUtil';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {Heading, LoadingSpinner, WindowSizeContext} from '@albert-io/atomic';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal';
import {buildGuideTree} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {
  getTeacherRestrictedGuideLevelIds,
  getGuideLevelIdsWithLockedDescendant
} from 'client/components/TeacherRestrictedContent';
import {StudyGuideItemList} from 'client/generic/StudyGuideItem/StudyGuideItem.react';
import AdvancedSearch from 'client/components/AdvancedSearch/AdvancedSearch.react';
import {getGuideLevelsBySearchParams} from 'client/components/AdvancedSearch/AdvancedSearch.queries';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import type {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';

import {showPublicViewForSubject} from 'components/PracticeView/practiceView.utils';

import {List} from 'immutable';

import {getSearchParams, isSearchMode} from '../components/AdvancedSearch/AdvancedSearch.utils';
import {hasStandardsQuery, hasTagsQuery} from '../components/AdvancedSearch/AdvancedSearch.queries';

import {GuideLevelCard} from './GuideLevelCard';
import MarketingBanner from './MarketingBanner/MarketingBanner.react';
import studyGuideUtils from './utils';
import {ThemeMenu} from './ThemeMenu/ThemeMenu';
import type {ThemeRefs} from './ThemeMenu/ThemeMenu';

import './study-guide.scss';

const NoResultsFound = () => (
  <div className='mt-x2 rounded-x1 border-border-primary background-primary p-x5 w-full border border-solid'>
    <Heading as='center' size='m'>
      No matches found
    </Heading>
  </div>
);

type StudyGuideLocation = {
  hash: string;
  pathname: string;
  search?: string;
  query: {
    s?: string;
    standards?: string;
    tags?: string;
  };
};

interface GuideLevelsListProps {
  guide: any;
  guideLevelsBySearchParams: any;
  location: StudyGuideLocation;
  subject: any;
  licensedSubjects: List<any>;
  usePublicView: boolean;
  showAdvancedSearch?: boolean;
  onSearchCallback?: (...args: any[]) => void;
  isReady?: boolean;
  partialCreditEnabled: boolean;
}

export const GuideLevelsList = ({
  guide,
  guideLevelsBySearchParams,
  location,
  subject,
  licensedSubjects,
  usePublicView,
  showAdvancedSearch,
  onSearchCallback,
  isReady,
  partialCreditEnabled
}: GuideLevelsListProps) => {
  // Directive withheld (annotation deferred, #19391): renders <AdvancedSearch>,
  // <ThemeMenu>, <UpgradeSubjectModal>, and <GuideLevelCard> rows whose subtrees still
  // read stores unbridged (out-of-area: #19405, client/generic #19411). Render-time
  // sessionStore reads are bridged below; the appStore.routerProps reads stay direct —
  // AppStore._setRouterProps uses writeDataNoEmit, so withRouter's location prop, not a
  // store emit, is the re-render trigger. The effect read stays (non-render).
  'use no memo';
  const hasTeacherAccess = useFluxStore(sessionStore, (s) => s.isTeacher());
  const [shouldShowUpgradeSubjectModal, setShouldShowUpgradeSubjectModal] = useState(false);
  const [hasPopover, setHasPopover] = useState(false);
  /**
   * Keyed on the subject id, not on the subject's identity: a late-settling Mandark query hands
   * down a fresh model for the same subject, and re-opening a modal the student already dismissed
   * on that pass is the bug this latch exists to prevent. The close handler leaves it alone.
   */
  const autoOpenedForSubjectId = useRef<string | null>(null);

  const {breakpoints, viewportWidth} = useContext(WindowSizeContext) as {
    breakpoints: Record<string, number>;
    viewportWidth: number;
  };

  const shouldDisplayGuideLevel = (guideLevel) => {
    if (!isSearchMode()) {
      return true;
    }
    const matchingGuideLevelIds = guideLevelsBySearchParams.cache('f', () => {
      return guideLevelsBySearchParams
        .getCollection()
        .toSet()
        .map((level) => level.getId());
    });
    return matchingGuideLevelIds.includes(guideLevel.getId());
  };

  /**
   * Deps are exactly the four inputs whose change must rebuild the theme list. The body
   * deliberately reads inputs that are NOT deps — `location.hash` and the search collection:
   * a hash change must not mint new theme refs (that would re-fire ThemeMenu's scroll effect on
   * every deep-link interaction), and a search collection settling late must not rebuild the
   * theme list. The rendered rows are recomputed per render below instead, which is what lets
   * them appear once the search settles.
   */
  // Hash and the search collection are read but deliberately untracked (see docstring)
  const {themes, themeRefs, initialTheme} = useMemo(() => {
    const displayedGuideLevels = guide
      .getSortedGuideLevels()
      .filter((guideLevel) => guideLevel.hasQuestions())
      .filter(shouldDisplayGuideLevel);
    const nextThemes = displayedGuideLevels.filter((guideLevel) => guideLevel.getNlevel() === 1);
    const nextThemeRefs: ThemeRefs = nextThemes.reduce((acc: ThemeRefs, theme) => {
      const ref = React.createRef();
      return {
        ...acc,
        [theme.getId()]: ref
      };
    }, {});

    // add scroll to slug behavior (see https://github.com/albert-io/project-management/issues/4140)
    const themeFromUrl = nextThemes.find(
      (theme) => theme.getUrlSlug() === location.hash.substring(1)
    );

    return {
      themes: nextThemes,
      themeRefs: nextThemeRefs,
      initialTheme: themeFromUrl?.getId()
    };
  }, [location?.query?.s, guide, subject, licensedSubjects]); // oxlint-disable-line react-hooks/exhaustive-deps -- see above

  // Auto-open at most once per subject id, surviving late query settles that hand down fresh
  // model identities — the ref latch is what makes an effect (not derived state) correct here.
  // oxlint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- one-shot side effect gated by the ref latch; fires when external query data settles, not a derivable value
  // Same four deps as the memo above, same rationale
  useEffect(() => {
    if (
      sessionStore.hasValidSession() &&
      !licensedSubjects.isEmpty() &&
      !subject.doesUserHaveAccess() &&
      autoOpenedForSubjectId.current !== subject.getId()
    ) {
      autoOpenedForSubjectId.current = subject.getId();
      setShouldShowUpgradeSubjectModal(true);
    }
  }, [location?.query?.s, guide, subject, licensedSubjects]);
  // oxlint-enable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change

  useEffect(() => {
    const handleScroll = () => {
      const el = document.querySelector('.study-guide__sticky-search--mobile');
      if (el) {
        // handles logic for adding the box shadow to the bottom of the stickied nav
        const stickyTop = parseInt(window.getComputedStyle(el).top, 10);
        const currentTop = el.getBoundingClientRect().top;
        const stickyClass = 'study-guide__sticky-search--is-sticky';
        if (currentTop === stickyTop) {
          el.classList.add(stickyClass);
        } else {
          el.classList.remove(stickyClass);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getMatchingQuestionCount = (guideLevel: any): number => {
    const matchingSearchResult = guideLevelsBySearchParams
      .getCollection()
      .find((result) => result.getId() === guideLevel.getId());

    return matchingSearchResult.getMeta().getTotalMatchingQuestions() || 0;
  };

  const openUpgradeSubjectModal = () => {
    setShouldShowUpgradeSubjectModal(true);
  };

  const closeUpgradeSubjectModal = () => {
    setShouldShowUpgradeSubjectModal(false);
  };

  const getStudyGuideLink = (
    linkToQuestions: string,
    queryString: string
  ): {pathname: string; search: string; state: {from: string}} => {
    const routerLocation = appStore.routerProps.location as {pathname: string; search: string};
    const currentRoute = routerLocation.pathname + routerLocation.search;
    return {
      pathname: linkToQuestions,
      search: queryString,
      state: {
        from: currentRoute
      }
    };
  };

  // role=button contract: Enter AND Space activate; keys bubbled from nested links/controls don't.
  const onKeyDown = (e: React.KeyboardEvent, onClick: (e: React.KeyboardEvent) => void) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  const allGuideLevels = guide.getSortedGuideLevels();
  const guideLevels = allGuideLevels
    .filter((guideLevel) => guideLevel.hasQuestions())
    .filter(shouldDisplayGuideLevel);
  // Tree built from the full, unfiltered guide-level list so the indicators + modal can derive
  // effective-lock state (a parent lock cascades down; all children locked rolls up).
  const guideLevelTree = buildGuideTree(
    allGuideLevels
      .toArray()
      .map((level) => ({id: level.getId(), parentId: level.getParentId() || null}))
  );
  // Teacher indicator: roll child locks up to a parent (a quiz whose question types are all locked
  // shows the indicator). Built from the question-bearing tree so it's stable across search/display.
  const teacherRestrictedGuideLevelIds = getTeacherRestrictedGuideLevelIds(
    allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
  );
  // DQA #17841: flag a parent theme that contains any locked guide level (any-descendant roll-up).
  const guideLevelIdsWithLockedDescendant = getGuideLevelIdsWithLockedDescendant(
    allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
  );
  const hasAccessToCreateTemplate = canCreateTemplateForSubject(subject.getId());
  const isMobileView = viewportWidth <= breakpoints.s;
  const hideThemeDropdownCTAText = viewportWidth <= breakpoints.xs;
  const isEmptySearches = isSearchMode() && guideLevelsBySearchParams.getCollection().isEmpty();
  const routerPathname = (appStore.routerProps.location as {pathname: string}).pathname;

  return (
    <div className='study-guide-wrapper'>
      <div
        className={classnames('study-guide__sticky-search', {
          // when popover backdrop is open, we need to increase the z-index of the sticky search by 1
          'study-guide__sticky-search--mobile': isMobileView,
          'study-guide__sticky-search--zIndex': hasPopover && viewportWidth <= breakpoints.xs
        })}
      >
        <AdvancedSearch
          showFilters={showAdvancedSearch}
          key={routerPathname}
          onSearchCallback={onSearchCallback}
          subjectId={subject.getId()}
          isMobileView={isMobileView}
          isEmptySearches={isEmptySearches}
          themes={themes}
          themeRefs={themeRefs}
          hideThemeDropdownCTAText={hideThemeDropdownCTAText}
          setHasPopover={setHasPopover}
        />
      </div>
      {!isReady ? (
        <LoadingIndicator />
      ) : (
        <div className='study-guide-content'>
          <div className='study-guide-content__split-pane'>
            <ThemeMenu
              themes={themes}
              themeRefs={themeRefs}
              initialTheme={initialTheme}
              hideSidebar={isMobileView}
            />
            {isEmptySearches ? (
              <NoResultsFound />
            ) : (
              <div className='study-guide-cards'>
                <StudyGuideItemList>
                  {guideLevels.map((guideLevel, i: number, displayedGuideLevels) => (
                    <GuideLevelCard
                      key={guideLevel.id}
                      guideLevel={guideLevel}
                      nextGuideLevel={displayedGuideLevels.get(i + 1)}
                      subject={subject}
                      licensedSubjects={licensedSubjects}
                      locationQuery={location.query}
                      usePublicView={usePublicView}
                      isAssessment={guide.getGuideType() === constants.GUIDE_TYPES.ASSESSMENT}
                      hasTeacherAccess={hasTeacherAccess}
                      hasAccessToCreateTemplate={hasAccessToCreateTemplate.value}
                      partialCreditEnabled={partialCreditEnabled}
                      guideLevelTree={guideLevelTree}
                      teacherRestrictedGuideLevelIds={teacherRestrictedGuideLevelIds}
                      guideLevelIdsWithLockedDescendant={guideLevelIdsWithLockedDescendant}
                      themeRefs={themeRefs}
                      getMatchingQuestionCount={getMatchingQuestionCount}
                      getStudyGuideLink={getStudyGuideLink}
                      openUpgradeSubjectModal={openUpgradeSubjectModal}
                      onKeyDown={onKeyDown}
                    />
                  ))}
                </StudyGuideItemList>
              </div>
            )}
          </div>
        </div>
      )}
      {shouldShowUpgradeSubjectModal && (
        <UpgradeSubjectModal subject={subject} handleClose={closeUpgradeSubjectModal} />
      )}
    </div>
  );
};

interface GuideLevelsListWithQueriesProps extends Omit<
  GuideLevelsListProps,
  'guide' | 'guideLevelsBySearchParams' | 'isReady' | 'licensedSubjects' | 'partialCreditEnabled'
> {
  user?: UserModelV2;
  router?: any;
  route?: any;
}

function GuideLevelsListWithQueries(props: GuideLevelsListWithQueriesProps) {
  // Directive withheld (annotation deferred, #19391): renders <GuideLevelsList>, itself
  // unannotatable (see its note). Render-time sessionStore reads bridged below; the
  // isSearchMode()/getSearchParams() reads are appStore-via-withRouter and stay direct.
  'use no memo';
  const hasValidSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const userId = useFluxStore(sessionStore, (s) => s.getUserId());
  const studentId = hasValidSession && isStudent ? userId : null;
  const {subject, location} = props;
  const params = getSearchParams();

  // Caching tags, standards, and subject access queries since we know they are going to be used by children components
  // This allows us to fetch everything in a single load time
  const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';

  const queries = [
    studyGuideUtils.getGuideBySubjectIdQuery(subject.getId(), guideType, studentId),
    userLicensedSubjectsQuery(),
    hasTagsQuery(subject.getId()),
    hasStandardsQuery(subject.getId()),
    getUserSubjectAccessQuery(subject.getId())
  ];

  if (hasValidSession) {
    queries.push(studyGuideUtils.getClassroomsByUserIdQuery(userId, isStudent));
  }

  if (isSearchMode()) {
    queries.push(
      getGuideLevelsBySearchParams({
        s: params.s,
        standards: params.standards,
        tags: params.tags,
        subjectId: subject.getId(),
        guideType
      })
    );
  }

  const {isReady, resources} = useMandarkQueries(queries);

  const [guide, licensedSubjects, __cachedTags, __cachedStandards, __cachedSubjectAccess, ...rest] =
    resources;

  let classrooms = List();
  let guideLevelsBySearchParams = List();

  if (hasValidSession && isSearchMode()) {
    [classrooms, guideLevelsBySearchParams] = rest;
  } else if (hasValidSession) {
    [classrooms] = rest;
  } else if (isSearchMode()) {
    [guideLevelsBySearchParams] = rest;
  }

  const partialCreditEnabled = studyGuideUtils.isPartialCreditEnabledForSubject(
    subject,
    classrooms,
    isStudent
  );

  return (
    <GuideLevelsList
      {...props}
      guide={guide}
      guideLevelsBySearchParams={guideLevelsBySearchParams}
      isReady={isReady}
      licensedSubjects={licensedSubjects}
      partialCreditEnabled={partialCreditEnabled}
    />
  );
}

const BootstrappedGuideLevelsList = withRouter(
  // `withApplicationMetadata` is untyped JS, so the prop bag it forwards has to be restated here
  // for the call site below.
  withApplicationMetadata(GuideLevelsListWithQueries, (props: GuideLevelsListWithQueriesProps) => {
    const guideType = props.location.pathname.includes('assessments') ? 'Assessment' : 'Practice';

    return {
      title: `${props.subject.getName()} | ${guideType} | Albert`,
      metaDescription: props.subject.getDescription()
    };
  }) as React.ComponentType<GuideLevelsListWithQueriesProps>
);

interface StudyGuideProps {
  subject: any;
  user?: UserModelV2;
  onSearchCallback?: (...args: unknown[]) => void;
  location: {pathname: string};
}

const StudyGuide = ({subject, user, onSearchCallback, location}: StudyGuideProps) => {
  // Directive withheld (annotation deferred, #19391): renders <MarketingBanner> and
  // <BootstrappedGuideLevelsList> (unannotatable, see above). Direct sessionStore reads
  // bridged below; isUnlicensedTeacherOrAdmin() (a lib/UserAccessUtil predicate that
  // fires a mandark query) and showPublicViewForSubject() (#19405) stay direct.
  'use no memo';

  const {isReady, value: isUnlicensedNonStudent} = isUnlicensedTeacherOrAdmin();
  const hasValidSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());

  const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';
  const isAssessment = guideType === 'assessment';
  const showAdvancedSearch = !(
    isAssessment &&
    (!hasValidSession || isUnlicensedNonStudent || isStudent)
  );

  // Dep-less on purpose: the gate flips when the access check becomes ready, and that pass
  // leaves `location.search` untouched, so search-keyed deps would never strip the params.
  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- clearing a filter the hidden search UI can't reach is a real side effect
    if (!isReady || showAdvancedSearch) {
      return;
    }
    const params = getQueryParamsAsObject();
    if (!isEmpty(params)) {
      removeQueryParams(...Object.keys(params));
    }
  });

  if (!isReady) {
    return <LoadingSpinner />;
  }

  const usePublicView = showPublicViewForSubject(subject);
  return (
    <>
      <MarketingBanner user={user} subject={subject} className='px-x2' />
      <BootstrappedGuideLevelsList
        subject={subject}
        user={user}
        usePublicView={usePublicView}
        showAdvancedSearch={showAdvancedSearch}
        onSearchCallback={onSearchCallback}
      />
    </>
  );
};

export default StudyGuide;
